import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import PageTemplate from '../../components/Common/PageTemplate';

const TurkiyeTatlisulariEnvanterKoruma = () => {
  const images = [
    'https://fs.hacettepe.edu.tr/fenyeni/resimler/_/_2/b6.jfif',
    'https://fs.hacettepe.edu.tr/fenyeni/resimler/_/_2/b7.jfif',
  ];

  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openModal = useCallback((index) => {
    setCurrentIndex(index);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  const nextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKey = (e) => {
      if (!modalOpen) return;
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [modalOpen, closeModal, nextImage, prevImage]);

  return (
    <PageTemplate
      title="Türkiye Tatlısu Balıklarının Güncel Envanteri ve Koruma Durumu Yayınlandı"
      subtitle=""
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl -mt-16 relative z-10 mb-16">

          {/* Başlık ve Badge'ler */}
          <div className="mb-8 space-y-3">
            <p className="text-2xl font-bold text-hacettepe-blue italic">Türkiye Tatlısu Balıklarının Listelenmesi Projesi</p>
            <div className="flex flex-wrap gap-2">
              <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-hacettepe-red text-white">Prof. Dr. F. Güler Ekmekçi</span>
              <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-blue-600 text-white">Doç. Dr. Baran Yoğurtçuoğlu</span>
              <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-emerald-600 text-white">Tarım ve Orman Bakanlığı / BSGM</span>
            </div>
          </div>

          {/* Metin */}
          <div className="space-y-5 mb-10">
            <p className="text-lg leading-relaxed text-gray-700">
              Tarım ve Orman Bakanlığı, Balıkçılık ve Su Ürünleri Genel Müdürlüğü (BSGM) ve Hacettepe Üniversitesi Fen Fakültesi Biyoloji Bölümü öğretim üyelerinden Prof. Dr. F. Güler Ekmekçi ve Doç. Dr. Baran Yoğurtçuoğlu'nun koordinasyon ve öncülüğünde yürütülen "Türkiye Tatlısu Balıklarının Listelenmesi" araştırma projesi tamamlanmıştır. Proje kapsamında elde edilen veriler, Türkiye'deki iç su balık çeşitliliğinin güncel taksonomik durumunu ve koruma statülerini kapsamlı bir şekilde ortaya koymuştur. Gerçekleştirilen projede Hacettepe Üniversitesi ve BSGM ile birlikte altı farklı üniversite, üç araştırma merkezi ve bir uluslararası müzeden toplam 22 akademisyen ve uzman görev almıştır.
            </p>
            <p className="text-lg leading-relaxed text-gray-700">
              Çalışma sonucunda Türkiye'nin tatlı su ekosistemlerinde 37 familyaya ait toplam 390 balık türü kaydedilmiştir. Bu türlerin 368'i yerli tür olarak kaydedilirken, 23 türün yerleşik popülasyonlara sahip yerli olmayan türler olduğu değerlendirilmiştir. Türkiye'de dağılım gösteren yerli türlerin 202'sinin (%55) Türkiye'ye özgü (endemik) türlerden oluştuğu belirlenmiştir.
            </p>
            <p className="text-lg leading-relaxed text-gray-700">
              Dünya Doğayı Koruma Birliği (IUCN) Kırmızı Liste kriterlerine göre yapılan değerlendirmelerde tüm türlerin yaklaşık %38'inin tehdit altında olduğu ortaya konmuştur. Türkiye'ye özgü türlerde ise bu oranın %57,5'i olduğu belirtilmiştir. Araştırma verilerine göre, Türkiye iç sularına özgü 6 balık türünün neslinin tükendiği tespit edilmiştir.
            </p>
            <p className="text-lg leading-relaxed text-gray-700">
              Proje ekibi tarafından yayınlanan bu çalışma, Türkiye'nin tatlı su biyoçeşitliliğinin korunmasına yönelik stratejilerin belirlenmesi ve su kaynaklarının yönetimi için güncel bir referans kaynağı niteliğindedir.
            </p>
          </div>

          {/* Görseller */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {images.map((src, i) => (
              <div
                key={i}
                className="group relative rounded-2xl overflow-hidden shadow-lg border border-gray-100 cursor-pointer"
                onClick={() => openModal(i)}
              >
                <img
                  src={src}
                  alt={`Türkiye Tatlısu Balıkları Envanteri - ${i + 1}`}
                  className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/50 rounded-full p-3">
                    <div className="w-5 h-5 border-2 border-white rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>

        <div className="text-center mb-16">
          <Link
            to="/haberler"
            className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-gray-800 to-gray-700 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-gray-300"
          >
            <FaArrowLeft className="mr-3 -ml-1 h-5 w-5 group-hover:-translate-x-1 transition-transform duration-300" />
            Tüm Haberlere Geri Dön
          </Link>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center"
          onClick={closeModal}
        >
          <button onClick={closeModal} className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-60">
            <FaTimes size={24} />
          </button>

          <div className="absolute top-4 left-4 text-white bg-black/50 px-3 py-1 rounded-full text-sm z-60">
            {currentIndex + 1} / {images.length}
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 text-white hover:text-gray-300 bg-black/50 rounded-full p-2 z-60"
          >
            <FaChevronLeft size={20} />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 text-white hover:text-gray-300 bg-black/50 rounded-full p-2 z-60"
          >
            <FaChevronRight size={20} />
          </button>

          <div
            className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[currentIndex]}
              alt={`Türkiye Tatlısu Balıkları Envanteri - ${currentIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </PageTemplate>
  );
};

export default TurkiyeTatlisulariEnvanterKoruma;
